export type ModuleId =
  | 'reference-data'
  | 'market-data'
  | 'instruments'
  | 'risk'
  | 'portfolio'
  | 'orchestrator'
  | 'alm'
  | 'wiki';

export interface ModuleConfig {
  id: ModuleId;
  label: string;
  accent: string;
  icon: string; // Lucide icon name
  description: string;
}

// Module accent colors — restrained for the white "traditional fin app" theme.
// Accents appear as a thin left rule on module cards / list items; they should
// stay muted and never compete with semantic P&L green/red on numeric data.
const NEUTRAL = '#1E3A8A';     // slate-blue (theme primary)
const RISK_RED = '#C8102E';    // P&L red — also used to mark the Risk module
const MUTED_TEAL = '#0F766E';  // single permitted teal note (ALM-only)

const HAND_MODULE_CONFIGS: Record<ModuleId, ModuleConfig> = {
  'reference-data': {
    id: 'reference-data',
    label: 'Reference Data',
    accent: NEUTRAL,
    icon: 'BookOpen',
    description: 'Calendars, conventions, currencies, and indexes',
  },
  'market-data': {
    id: 'market-data',
    label: 'Market Data',
    accent: NEUTRAL,
    icon: 'TrendingUp',
    description: 'Curves, vol surfaces, quotes, and fixings',
  },
  instruments: {
    id: 'instruments',
    label: 'Instruments',
    accent: NEUTRAL,
    icon: 'FileText',
    description: 'Bonds, swaps, options, CDS, and FX instruments',
  },
  risk: {
    id: 'risk',
    label: 'Risk Analytics',
    accent: RISK_RED,
    icon: 'Shield',
    description: 'Pricing, Greeks, scenarios, and stress testing',
  },
  portfolio: {
    id: 'portfolio',
    label: 'Portfolio',
    accent: NEUTRAL,
    icon: 'PieChart',
    description: 'Portfolio management and position analysis',
  },
  orchestrator: {
    id: 'orchestrator',
    label: 'Orchestrator',
    accent: NEUTRAL,
    icon: 'Bot',
    description: 'AI agents, chat, and workflow orchestration',
  },
  alm: {
    id: 'alm',
    label: 'BSM & Risk Mngt',
    accent: MUTED_TEAL,
    icon: 'Landmark',
    description: 'Balance-sheet management & risk — IRRBB, liquidity analytics, NMD behavioral modeling',
  },
  wiki: {
    id: 'wiki',
    label: 'Knowledge Wiki',
    accent: NEUTRAL,
    icon: 'BookMarked',
    description: 'LLM-maintained knowledge base — ingest, query, lint',
  },
};

// ── Generated-module merge ─────────────────────────────────────────────────
// Modules born from `codaic add service` arrive in the generated registry; the
// hand entries above keep their curated colours and copy, and anything the
// registry knows that this file does not appears with the registry's defaults.
// This is what lets a new backend service reach the portal without anyone
// editing this file again.
import { MODULE_CONFIGS as GENERATED_MODULE_CONFIGS } from '../registry/portalRegistry';

// Generated module ids that are naming variants of a hand-curated module.
// An aliased module contributes its CONTENT (nav entries, entity pages) to the
// hand module instead of appearing as a second card and sidebar section —
// instrument-service derives module id 'instrument', but this portal has
// always called that module 'instruments'.
export const SECTION_ALIASES: Record<string, string> = {
  instrument: 'instruments',
};

export const MODULE_CONFIGS: Record<string, ModuleConfig> = Object.fromEntries(
  Object.entries({
    ...(GENERATED_MODULE_CONFIGS as unknown as Record<string, ModuleConfig>),
    ...HAND_MODULE_CONFIGS,
  }).filter(([id]) => !(id in SECTION_ALIASES)),
);

export const MODULE_LIST: ModuleConfig[] = Object.values(MODULE_CONFIGS);
